import styled, { css } from 'styled-components';

import { ContentWrapperContainer } from '../../ContentWrapper/index.styles';
import { FIELD_LABEL_WIDTH, TABLE_HEADER_HEIGHT } from '../constants';

const Row = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1.5)};
`;

const SpacedRow = styled(Row)`
  justify-content: space-between;
`;

const CenteredRow = styled(Row)`
  justify-content: center;
`;

// The group above fills the width, so a max-width'd page centres itself the
// way a centring PageContainer centres the loaded ContentWrapper.
const PageRoot = styled(ContentWrapperContainer)`
  margin-inline: auto;
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1 1 auto;
  gap: ${({ theme }) => theme.spacing(0.5)};
`;

/** Matches the fixed label gutter of a `CommitForm` field. */
const FieldLabel = styled.div`
  min-width: ${FIELD_LABEL_WIDTH}px;
  width: ${FIELD_LABEL_WIDTH}px;
  max-width: ${FIELD_LABEL_WIDTH}px;
`;

const FieldValue = styled.div`
  flex: 1 1 auto;
  min-width: 0;
`;

/** Mirrors the `DataGrid` header: same height, same bold column labels. */
const TableHeader = styled(Row)`
  height: ${TABLE_HEADER_HEIGHT}px;
  padding: ${({ theme }) => theme.spacing(0, 1.25)};
  gap: ${({ theme }) => theme.spacing(2.5)};
`;

interface TableRowProps {
  $height: number;
  $first: boolean;
  $last: boolean;
}

/**
 * Mirrors a `DataGrid` body row, including the striping and the block rounding
 * applied to the first and last rows rather than to every row.
 */
const TableRow = styled(Row)<TableRowProps>`
  height: ${({ $height }) => $height}px;
  padding: ${({ theme }) => theme.spacing(0, 1.25)};
  gap: ${({ theme }) => theme.spacing(2.5)};

  &:nth-child(even) {
    background-color: ${({ theme }) => theme.palette.background.canvas};
  }
  &:nth-child(odd) {
    background-color: ${({ theme }) => theme.palette.backdrop.main};
  }

  ${({ theme, $first }) =>
    $first &&
    css`
      border-top-left-radius: ${theme.shape.borderRadius}px;
      border-top-right-radius: ${theme.shape.borderRadius}px;
    `}
  ${({ theme, $last }) =>
    $last &&
    css`
      border-bottom-left-radius: ${theme.shape.borderRadius}px;
      border-bottom-right-radius: ${theme.shape.borderRadius}px;
    `}
`;

interface TableCellProps {
  $flex: number;
}

const TableCell = styled.div<TableCellProps>`
  flex: ${({ $flex }) => $flex} 1 0;
  min-width: 0;
`;

interface ListRowProps {
  $height?: number;
  $divided: boolean;
}

const ListRow = styled(Row)<ListRowProps>`
  ${({ $height }) =>
    $height !== undefined &&
    css`
      min-height: ${$height}px;
    `}
  padding: ${({ theme }) => theme.spacing(1, 0)};

  ${({ theme, $divided }) =>
    $divided &&
    css`
      border-bottom: 1px solid ${theme.palette.divider};

      &:last-of-type {
        border-bottom: none;
      }
    `}
`;

const Stack = styled.div<{ $gap: number }>`
  display: flex;
  flex-direction: column;
  gap: ${({ theme, $gap }) => theme.spacing($gap)};
  width: 100%;
`;

/** Plot area of a chart skeleton: bars sit on the baseline. */
const Plot = styled.div<{ $height: number }>`
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(1)};
  height: ${({ $height }) => $height}px;
  width: 100%;
`;

const Axis = styled(Row)`
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(1)};
  padding-top: ${({ theme }) => theme.spacing(1)};
`;

const CalendarGrid = styled.div<{ $columns: number }>`
  display: grid;
  grid-template-columns: repeat(${({ $columns }) => $columns}, minmax(0, 1fr));
  gap: ${({ theme }) => theme.spacing(1)};
  width: 100%;
`;

// Mirrors WeekCalendar's own toolbar: left-aligned, with its own padding.
const CalendarToolbarRow = styled(Row)`
  gap: ${({ theme }) => theme.spacing(1)};
  padding: ${({ theme }) => theme.spacing(1, 2)};
`;

const CalendarColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1)};
`;

const Toolbar = styled(Row)`
  width: 100%;
  margin-bottom: ${({ theme }) => theme.spacing(1)};
  gap: ${({ theme }) => theme.spacing(1)};
`;

const ToolbarSearch = styled.div`
  flex: 1 1 auto;
  min-width: 0;
`;

const TabsRow = styled(Row)`
  justify-content: center;
  gap: ${({ theme }) => theme.spacing(4)};
  padding-bottom: ${({ theme }) => theme.spacing(4)};
`;

export {
  Axis,
  CalendarColumn,
  CalendarGrid,
  CalendarToolbarRow,
  CenteredRow,
  Column,
  FieldLabel,
  FieldValue,
  ListRow,
  PageRoot,
  Plot,
  Row,
  SpacedRow,
  Stack,
  TableCell,
  TableHeader,
  TableRow,
  TabsRow,
  Toolbar,
  ToolbarSearch,
};
